import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Menu, FileText, MessageSquare, RefreshCw, Download, AlertCircle, Building2, Calendar, Clock, FolderOpen, ClipboardList, TrendingUp, FileSearch, Presentation } from 'lucide-react';
import './App.css';
import SharedHeader from './components/SharedHeader';
import TitleCard from './components/TitleCard';
import LoadingOverlay from './components/LoadingOverlay';
import ChatHome from './components/ChatHome';
import NavigationSidebar from './components/NavigationSidebar';
import ModernSidebar from './components/ModernSidebar';
import ContentNavigation from './components/ContentNavigation';
import BoardDeckDraftingAssistant from './components/BoardDeckDraftingAssistant';
import VarianceAnalysis from './components/VarianceAnalysis';
import ModernVarianceAnalysis from './components/ModernVarianceAnalysis';
import ModernFileManager from './components/ModernFileManager';
import UnstructAIPage from './components/UnstructAIPage';
import ModernTranscriptAnalysis from './components/ModernTranscriptAnalysis';
import ModernPresentationAnalysis from './components/ModernPresentationAnalysis';
import ModernBoardDeckGenerator from './components/ModernBoardDeckGenerator';
import LandingPage from './components/LandingPage';
import { CountryProvider, useCountry } from './context/CountryContext';
import config, { getDefaultHeaders } from './config/api';

function AppContent() {
  const { country } = useCountry();
  const [activePage, setActivePage] = useState('landing');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [presentationContent, setPresentationContent] = useState('');
  const [transcriptContent, setTranscriptContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isPageLoading, setIsPageLoading] = useState(false);
  
  // Conversation state
  const [currentConversationId, setCurrentConversationId] = useState(null);
  const [loadedMessages, setLoadedMessages] = useState([]);
  
  // New state for toggle buttons
  const [availableOptions, setAvailableOptions] = useState({
    banks: [],
    years: [],
    quarters: [],
    segments: []
  });
  const [selectedOptions, setSelectedOptions] = useState({
    bank: '',
    year: '',
    quarter: '',
    segment: ''
  });

  // Section navigation state
  const [activeSection, setActiveSection] = useState(null);

  const API_BASE_URL = config.apiUrl;

  useEffect(() => {
    // Only load available options if:
    // 1. Country is selected
    // 2. We're on a page that needs the options (presentation or transcript)
    // This ensures API is only called when user navigates to these pages, not on initial load or landing page
    const needsOptions = activePage === 'presentation' || activePage === 'transcript';
    
    if (country && needsOptions) {
      // Only load when on pages that need options
      loadAvailableOptions();
    } else {
      // Reset options when not on pages that need them or country not selected
      setAvailableOptions({
        banks: [],
        years: [],
        quarters: [],
        segments: []
      });
      setSelectedOptions({
        bank: '',
        year: '',
        quarter: '',
        segment: ''
      });
    }
  }, [country, activePage]);

  useEffect(() => {
    // Load content when selections change (only for presentation and transcript pages)
    if (selectedOptions.bank && selectedOptions.year && selectedOptions.quarter && (activePage === 'presentation' || activePage === 'transcript')) {
      loadSelectedContent();
    }
  }, [selectedOptions, activePage]);

  // Page loading effect for presentation and transcript pages
  useEffect(() => {
    if (activePage === 'presentation' || activePage === 'transcript') {
      setIsPageLoading(true);
      const timer = setTimeout(() => {
        setIsPageLoading(false);
      }, 1500);

      return () => clearTimeout(timer);
    }
  }, [activePage]);

  // Add scroll event listener to track active section
  useEffect(() => {
    const handleScroll = () => {
      const elements = document.querySelectorAll('h1, h2, h3');
      let currentSection = null;
      
      elements.forEach((element, index) => {
        const rect = element.getBoundingClientRect();
        if (rect.top <= 100) { // 100px offset from top
          currentSection = `section-${index}`;
        }
      });
      
      if (currentSection && currentSection !== activeSection) {
        setActiveSection(currentSection);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [activeSection]);

  const loadAvailableOptions = async () => {
    // Don't load if country is not selected
    if (!country) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/output-mapping`, {
        headers: getDefaultHeaders(country)
      });
      
      if (!response.ok) {
        // Reset options when API fails
        setAvailableOptions({
          banks: [],
          years: [],
          quarters: [],
          segments: []
        });
        setSelectedOptions({
          bank: '',
          year: '',
          quarter: '',
          segment: ''
        });
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      setAvailableOptions(data.available_options);
      
      // Set default selections (latest year/quarter, first bank, key_highlights for presentation)
      if (data.available_options.banks.length > 0 && 
          data.available_options.years.length > 0 && 
          data.available_options.quarters.length > 0) {
        
        const defaultBank = data.available_options.banks[0];
        const defaultYear = data.available_options.years[0]; // Latest year
        const defaultQuarter = data.available_options.quarters[0]; // First quarter
        const defaultSegment = data.available_options.segments.length > 0 ? 
          data.available_options.segments.find(s => s.toLowerCase().includes('key')) || 
          data.available_options.segments[0] : '';
        
        setSelectedOptions({
          bank: defaultBank,
          year: defaultYear,
          quarter: defaultQuarter,
          segment: defaultSegment
        });
      } else {
        // If no data available, reset selections
        setSelectedOptions({
          bank: '',
          year: '',
          quarter: '',
          segment: ''
        });
      }
      
    } catch (error) {
      console.error('Error loading available options:', error);
      // Ensure options are reset on error
      setAvailableOptions({
        banks: [],
        years: [],
        quarters: [],
        segments: []
      });
      setSelectedOptions({
        bank: '',
        year: '',
        quarter: '',
        segment: ''
      });
      setError('Failed to load available options. Please ensure the output mapping is generated for the selected country.');
    }
  };

  const loadSelectedContent = async () => {
    if (!selectedOptions.bank || !selectedOptions.year || !selectedOptions.quarter) {
      return;
    }

    setLoading(true);
    setError(null);
    setActiveSection(null); // Reset active section when loading new content
    
    try {
      const params = new URLSearchParams({
        bank: selectedOptions.bank,
        year: selectedOptions.year,
        quarter: selectedOptions.quarter,
        document_type: activePage === 'presentation' ? 'presentation' : 'transcript'
      });
      
      // Add segment for presentation analysis
      if (activePage === 'presentation' && selectedOptions.segment) {
        params.append('segment', selectedOptions.segment);
      }
      
      const response = await fetch(`${API_BASE_URL}/summary-file?${params}`, {
        headers: getDefaultHeaders(country)
      });
      
      if (!response.ok) {
        if (response.status === 404) {
          const errorData = await response.json();
          throw new Error(errorData.detail);
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (activePage === 'presentation') {
        setPresentationContent(data.content);
        setLastUpdated(data.last_modified);
      } else {
        setTranscriptContent(data.content);
        setLastUpdated(data.last_modified);
      }
      
    } catch (error) {
      console.error('Error loading selected content:', error);
      const errorMessage = error.message || 'Failed to load selected content.';
      setError(errorMessage);
      
      // Set fallback content
      const fallbackContent = `# No ${activePage === 'presentation' ? 'Presentation' : 'Transcript'} Analysis Available\n\n${errorMessage}\n\nPlease check your selections or ensure the files exist.`;
      
      if (activePage === 'presentation') {
        setPresentationContent(fallbackContent);
      } else {
        setTranscriptContent(fallbackContent);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOptionChange = (optionType, value) => {
    setSelectedOptions(prev => ({
      ...prev,
      [optionType]: value
    }));
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    // Reset segment selection when switching to transcript (no segments needed)
    if (tab === 'transcript') {
      setSelectedOptions(prev => ({ ...prev, segment: '' }));
    } else if (tab === 'presentation') {
      // Set default segment for presentation if none is selected
      setSelectedOptions(prev => {
        if (!prev.segment && availableOptions.segments.length > 0) {
          const defaultSegment = availableOptions.segments.find(s => s.toLowerCase().includes('key')) || 
                                availableOptions.segments[0];
          return { ...prev, segment: defaultSegment };
        }
        return prev;
      });
    }
  };

  const downloadMarkdown = async (filename) => {
    try {
      const response = await fetch(`${API_BASE_URL}/download/${filename}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('Error downloading file:', error);
      setError('Failed to download file. Please try again.');
    }
  };

  const getCurrentContent = () => {
    return activePage === 'presentation' ? presentationContent : transcriptContent;
  };

  const getCurrentFilename = () => {
    return activePage === 'presentation' ? 'presentation_analysis.md' : 'transcript_analysis.md';
  };

  const formatLastUpdated = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const handleSectionClick = (section) => {
    // Find the section element in the DOM and scroll to it
    const sectionElement = document.querySelector(`h1, h2, h3`);
    if (sectionElement) {
      // Find the element that matches our section
      const elements = document.querySelectorAll('h1, h2, h3');
      for (let i = 0; i < elements.length; i++) {
        if (elements[i].textContent.trim() === section.title) {
          elements[i].scrollIntoView({ 
            behavior: 'smooth', 
            block: 'start' 
          });
          setActiveSection(section.id);
          break;
        }
      }
    }
  };

  // Handle conversation loading from sidebar
  const handleLoadConversation = (conversationId, messages) => {
    setCurrentConversationId(conversationId);
    setLoadedMessages(messages);
    setActivePage('chat');
  };

  // Navigation handler for landing page
  const handleNavigateToChat = () => {
    setActivePage('chat');
  };

  // Render content based on active tab
    const renderContent = () => {
    if (activePage === 'landing') {
      return <LandingPage onNavigateToChat={handleNavigateToChat} />;
    }
    
    if (activePage === 'chat') {
      return <ChatHome 
        onMenuClick={() => setSidebarOpen(true)} 
        onNavigateHome={() => setActivePage('chat')}
        conversationId={currentConversationId}
        loadedMessages={loadedMessages}
        onClearConversation={() => {
          setCurrentConversationId(null);
          setLoadedMessages([]);
        }}
      />;
    }
    
    if (activePage === 'transcript') {
      return (
        <ModernTranscriptAnalysis
          availableOptions={availableOptions}
          selectedOptions={selectedOptions}
          onOptionChange={handleOptionChange}
          transcriptContent={transcriptContent}
          loading={loading}
          error={error}
          onGenerate={loadSelectedContent}
        />
      );
    }
    
    if (activePage === 'presentation') {
      return (
        <ModernPresentationAnalysis
          availableOptions={availableOptions}
          selectedOptions={selectedOptions}
          onOptionChange={handleOptionChange}
          presentationContent={presentationContent}
          loading={loading}
          error={error}
          onGenerate={loadSelectedContent}
        />
      );
    }
    
    if (activePage === 'board-deck') {
      return <ModernBoardDeckGenerator />;
    }

    if (activePage === 'variance') {
      return <ModernVarianceAnalysis />;
    }

    if (activePage === 'file-manager') {
      return <ModernFileManager />;
    }

    if (activePage === 'unstruct-ai') {
      return <UnstructAIPage />;
    }

    // Default fallback for any other pages
    return (
      <div className="page-not-found">
        <h2>Page Not Found</h2>
        <p>The requested page could not be found.</p>
        </div>
    );
  };

  return (
    <div className="app">
      {/* Modern Sidebar - Only visible when not on landing page */}
      {activePage !== 'landing' && (
        <ModernSidebar 
          activePage={activePage}
          onPageChange={setActivePage}
          onLoadConversation={handleLoadConversation}
        />
      )}

      {/* Main Content */}
      <div className={`main-content ${activePage === 'landing' ? 'landing-main-content' : ''}`}>
        {renderContent()}
      </div>
    </div>
  );
}

function App() {
  return (
    <CountryProvider>
      <AppContent />
    </CountryProvider>
  );
}

export default App; 